import { observer } from 'mobx-react';
import { productPropTypes } from '@nandosaus/prop-types';
import { useRootStore } from '@nandosaus/state-management';
import PropTypes from 'prop-types';
import React from 'react';
import styled, { css } from 'styled-components';

import { ANALYTICS_EVENTS } from '@nandosaus/constants';
import { analytics } from '../../analytics';
import { Box, Flex } from '../grid';
import { Label, P } from '../typography';
import { PerksShopProductDetailModal } from '../perks-shop-product-detail-modal';
import { TextClamp } from '../text-clamp';

// Styled components sit above component otherwise storybook crashes
const Pricing = styled(Flex)``;

// Styles are defined within Wrapper
const EditItem = styled(Box)``;

const Wrapper = styled(Flex)`
  min-height: 75px;

  :not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.background};
  }

  ${({ clickDisabled }) =>
    !clickDisabled &&
    css`
      cursor: pointer;

      ${EditItem} {
        width: 0px;
        visibility: hidden;
        opacity: 0;
        transition: visibility 0s, opacity 0.2s linear;
      }

      :hover {
        ${Pricing} {
          display: none;
        }

        ${EditItem} {
          visibility: visible;
          opacity: 1;
          min-width: fit-content;
          min-height: 42px;
        }
      }
    `}
`;

const ItemList = ({ items, disableOnClick }) => {
  const { PerksShopStore } = useRootStore();
  const { setSelectedItem, selectedItem, clearSelectedItem, updateCartItem, removeCartItem } = PerksShopStore;

  const handleRemoveFromCart = item => {
    removeCartItem(item);
    clearSelectedItem();
  };

  const updateItem = (item, selectedVariant, quantity) => {
    updateCartItem(item, selectedVariant, quantity);
    clearSelectedItem();
  };

  const handleOnClick = item => {
    if (!disableOnClick) {
      const { product, quantity, selectedVariant } = item;
      setSelectedItem({ cartItem: item.id, product: product.id, quantity, selectedVariant });

      analytics.track(ANALYTICS_EVENTS.PRODUCT_UPDATE_CLICKED, { product: item.name });
    }
  };

  return (
    <>
      <Flex flex={1} flexDirection="column">
        {items.map(item => {
          const { quantity, product, selectedVariant } = item;
          const { pointsPrice, heading } = product;

          return (
            <Wrapper
              justifyContent="space-between"
              py={1}
              clickDisabled={disableOnClick}
              onClick={() => handleOnClick(item)}
              key={selectedVariant.plu}
            >
              <Flex mr={1} width="70%">
                <Flex
                  alignItems="center"
                  borderColor="grey300"
                  borderStyle="solid"
                  borderWidth="1px"
                  height="1.5rem"
                  justifyContent="center"
                  mr="0.5rem"
                  minWidth="1.5rem"
                >
                  <P variant={3}>{quantity}</P>
                </Flex>
                <Box>
                  <Label>
                    <TextClamp>{heading.trim()}</TextClamp>
                  </Label>
                  {Object.keys(selectedVariant).map(key => {
                    if (key === 'plu' || key === 'stock' || !selectedVariant[key]) {
                      return null;
                    }
                    return (
                      <P variant={3} color="greyPrimary" mt="0.25rem" key={selectedVariant[key].toString()}>
                        {selectedVariant[key].toString()}
                      </P>
                    );
                  })}
                </Box>
              </Flex>

              {!disableOnClick && (
                <EditItem>
                  <P variant={3} color="infoPrimary" textAlign="right">
                    Edit Item
                  </P>
                </EditItem>
              )}
              <Pricing alignItems="flex-end" flexDirection="column" flexShrink={0}>
                <P variant={3}>{pointsPrice * quantity} points</P>
              </Pricing>
            </Wrapper>
          );
        })}
      </Flex>
      {!!selectedItem && (
        <PerksShopProductDetailModal
          handleSubmit={updateItem}
          modalOpen={!!selectedItem.product}
          onClose={clearSelectedItem}
          handleRemoveFromCart={handleRemoveFromCart}
        />
      )}
    </>
  );
};

ItemList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      quantity: PropTypes.number.isRequired,
      product: productPropTypes,
    })
  ).isRequired,
  disableOnClick: PropTypes.bool,
};

ItemList.defaultProps = {
  disableOnClick: false,
};

const ComposedOrderItemList = observer(ItemList);
export { ComposedOrderItemList as ItemList };
